import { useEffect, useState } from 'react';
import { ArrowLeft, Flag, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import AssetLossPanel from './AssetLossPanel';
import OrderAttentionPanel from '../orders/OrderAttentionPanel';
import OrderHistory from '../orders/OrderHistory';
import { businessDeliveryOperationsService as deliveryApi } from '../../services/businessDeliveryOperationsService';
import { businessFulfillmentService } from '../../services/businessFulfillmentService';
import { orderAttention } from '../../utils/orderAttention';

// One parcel as a record: what it is, where it is now, what happened to it,
// and what it is linked to. The parcel list item is the base; the order's
// history and the trip stop are added only for people allowed to read them.

const readable = (value) => String(value || '').replaceAll('_', ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
export const PARCEL_STATUS = Object.freeze({
  PENDING: 'Not sent out yet', READY_FOR_DISPATCH: 'Ready for a rider', IN_TRANSIT: 'With the rider',
  OUT_FOR_DELIVERY: 'Out for delivery', DELIVERED: 'Delivered', FAILED: 'Delivery failed',
  RETURNED: 'Returned to the store', CANCELLED: 'Cancelled',
});
export const ASSIGNMENT_STATUS = Object.freeze({
  COMMITTED: 'Assigned, waiting for pickup', PICKED_UP: 'With the rider', COMPLETED: 'Finished', CANCELLED: 'Assignment ended',
});
const STOP_STATUS = Object.freeze({
  QUEUED: 'Waiting in the trip', ACTIVE: 'Rider heading there', ARRIVED: 'Rider at the customer', DELIVERED: 'Delivered',
  ATTEMPT_FAILED: 'Attempt failed', RESCHEDULED: 'Rescheduled', CANCELLED: 'Removed from the trip',
});
const PROOF_STATUS = Object.freeze({ FINALIZED: 'Photo saved', APPROVED: 'Photo approved', REJECTED: 'Photo rejected' });
export const parcelStatusLabel = (status) => PARCEL_STATUS[status] || readable(status) || 'Status unavailable';

const peso = (cents) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(Number(cents || 0) / 100);
const dateTime = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : new Intl.DateTimeFormat('en-PH', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
};
export function paymentFact(collection) {
  if (collection?.status === 'COLLECT') return `Cash on delivery: ${peso(collection.amountCents)} to collect`;
  if (collection?.status === 'PAID') return 'Paid, nothing to collect';
  return 'Payment needs review';
}

const panel = 'rounded-2xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950';
const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus';
const textLink = `font-semibold underline underline-offset-4 ${focusRing}`;

function Fact({ term, children }) {
  if (children == null || children === '') return null;
  return <div className="min-w-0"><dt className="text-xs text-neutral-600 dark:text-neutral-400">{term}</dt><dd className="mt-0.5 break-words text-sm font-medium">{children}</dd></div>;
}

export default function ParcelRecord({ parcel, businessId, can, busy, onBack, onClearHold, onChanged }) {
  const canReadOrders = can('orders:read');
  const [order, setOrder] = useState(null);
  const [orderState, setOrderState] = useState(canReadOrders ? 'loading' : 'restricted');
  const [stop, setStop] = useState(null);
  const [lossOpen, setLossOpen] = useState(false);

  useEffect(() => {
    let live = true;
    setOrder(null); setStop(null); setLossOpen(false);
    if (canReadOrders) {
      setOrderState('loading');
      businessFulfillmentService.detail(parcel.orderId)
        .then((next) => { if (live) { setOrder(next); setOrderState('ready'); } })
        .catch(() => { if (live) setOrderState('error'); });
    } else setOrderState('restricted');
    if (parcel.runId) {
      deliveryApi.run(businessId, parcel.runId)
        .then((run) => { if (live) setStop((run?.stops || []).find((item) => Number(item.shipmentId) === Number(parcel.id)) || null); })
        .catch(() => {});
    }
    return () => { live = false; };
  }, [businessId, canReadOrders, parcel.id, parcel.orderId, parcel.runId, parcel.version]);

  const delivery = parcel.delivery || {};
  const assignment = parcel.assignment;
  const proof = stop?.attempt?.proof || null;
  // An ended assignment and a stop removed from its trip are history, not the
  // parcel's current rider or place in a route.
  const assignmentEnded = assignment?.status === 'CANCELLED';
  const stopRemoved = stop?.status === 'CANCELLED';
  const labelCode = order?.parcelLabel?.code || stop?.parcelLabelCode || null;
  // On this page the reader is already on the parcel, so that link is dropped.
  const attention = order ? orderAttention(order) : null;
  const here = `parcel=${parcel.id}`;
  const shownAttention = attention ? { ...attention, actions: attention.actions.filter((action) => !action.to.includes(here)) } : null;
  // A hold someone typed by hand is cleared here, by whoever may release pickups.
  const manualHold = parcel.holdReason && !/^[A-Z0-9_]+$/.test(parcel.holdReason) ? parcel.holdReason : null;
  const codedHoldWithoutOrder = parcel.holdReason && !manualHold && !order;
  const canWriteOff = can('delivery.exceptions.correct.business') && assignment?.status === 'PICKED_UP';
  const links = [
    canReadOrders && [`/business/orders/record/${parcel.orderId}`, 'Order record'],
    parcel.runId && [`/business/delivery?view=trips&trip=${parcel.runId}`, `Trip #${parcel.runId}`],
    proof && can('delivery.proofs.review.business') && [`/business/delivery/proofs?show=all&proof=${proof.id}`, 'Delivery proof'],
    assignment?.riderId && can('delivery.riders.read.business') && [`/business/delivery?view=riders&rider=${assignment.riderId}`, assignment.riderName || 'Rider'],
  ].filter(Boolean);

  return (
    <article aria-labelledby="parcel-record-title" className="space-y-4">
      <button type="button" onClick={onBack} className={`inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-secondary hover:text-accent ${focusRing}`}><ArrowLeft className="h-4 w-4" aria-hidden="true" />Back to parcels</button>

      <section className={`p-4 sm:p-5 ${panel}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id="parcel-record-title" className="break-words text-xl font-semibold">{parcel.reference || `Parcel #${parcel.id}`}</h2>
            <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">Parcel #{parcel.id}{labelCode ? ` · Label ${labelCode}` : ''}</p>
          </div>
          <span className="inline-flex shrink-0 rounded-full bg-neutral-100 px-3 py-1 text-sm font-semibold dark:bg-white/10">{parcelStatusLabel(parcel.status)}</span>
        </div>

        <dl className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <Fact term="Rider">{!assignment ? 'No rider assigned' : assignmentEnded ? `None now (last: ${assignment.riderName || `Rider #${assignment.riderId}`})` : (assignment.riderName || `Rider #${assignment.riderId}`)}</Fact>
          <Fact term="Assignment">{assignment ? (ASSIGNMENT_STATUS[assignment.status] || readable(assignment.status)) : 'Not assigned'}</Fact>
          <Fact term="Trip">{!parcel.runId ? 'Not on a trip' : stopRemoved || assignmentEnded ? `Removed from trip #${parcel.runId}` : `Trip #${parcel.runId}${stop ? ` · stop ${stop.sequence}` : ''}`}</Fact>
          <Fact term="Payment">{paymentFact(delivery.collection)}</Fact>
          {stop && !stopRemoved ? <Fact term="Stop">{STOP_STATUS[stop.status] || readable(stop.status)}</Fact> : null}
          {stop?.attempt ? <Fact term="Delivery attempt">{`Attempt ${stop.attempt.number}${stop.attempt.status === 'FAILED' ? ', failed' : stop.attempt.status === 'DELIVERED' ? ', delivered' : ''}`}</Fact> : null}
          {stop && !stopRemoved ? <Fact term="Proof of delivery">{proof
            ? <span className="inline-flex flex-wrap items-center gap-2">{PROOF_STATUS[proof.status] || 'Recorded'}{proof.reviewRequired ? <span className="inline-flex items-center gap-1 font-semibold text-warning"><Flag className="h-3 w-3" aria-hidden="true" />Flagged</span> : null}</span>
            : 'No photo yet'}</Fact> : null}
          {stop?.completedAt ? <Fact term="Stop closed">{dateTime(stop.completedAt)}</Fact> : null}
        </dl>

        {links.length ? (
          <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-1 border-t border-neutral-200 pt-3 text-sm dark:border-neutral-800">
            {links.map(([to, title]) => <li key={to}><Link to={to} className={`inline-flex min-h-9 items-center ${textLink}`}>{title}</Link></li>)}
          </ul>
        ) : null}
      </section>

      {shownAttention && !(manualHold && shownAttention.reason) ? <OrderAttentionPanel order={order} attention={shownAttention} /> : null}
      {codedHoldWithoutOrder ? <p className="rounded-xl border border-warning bg-warning-subtle p-4 text-sm text-warning">This parcel is on hold and will not be sent out. The reason is on its order record.</p> : null}
      {manualHold ? (
        <section aria-labelledby="parcel-hold-title" className="rounded-xl border border-warning bg-warning-subtle p-4 text-sm text-warning">
          <h3 id="parcel-hold-title" className="text-base font-semibold">Pickup is on hold</h3>
          <p className="mt-1">This parcel will not be handed to a rider until the hold is cleared.</p>
          <p className="mt-2 break-words">Reason given: <q>{manualHold}</q></p>
          {can('delivery.pickups.release.business')
            ? <button type="button" disabled={busy} onClick={onClearHold} className="mt-3 inline-flex min-h-11 items-center rounded-lg bg-accent px-4 font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50">Confirm physically ready and clear hold</button>
            : <p className="mt-2 font-medium">Someone who can release pickups needs to clear this hold.</p>}
        </section>
      ) : null}

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <section aria-labelledby="parcel-recipient-title" className={`p-4 ${panel}`}>
          <h3 id="parcel-recipient-title" className="font-semibold">Recipient</h3>
          <dl className="mt-3 grid gap-3">
            <Fact term="Name">{delivery.recipientName || 'Not recorded'}</Fact>
            <Fact term="Phone">{delivery.recipientPhone ? <a className={`inline-flex min-h-9 items-center ${textLink}`} href={`tel:${delivery.recipientPhone.replace(/[^+0-9]/g, '')}`}>{delivery.recipientPhone}</a> : 'Not recorded'}</Fact>
            <Fact term="Address">{delivery.address || 'Not recorded'}</Fact>
            <Fact term="Delivery instructions">{delivery.instructions}</Fact>
          </dl>
        </section>

        <section className={`p-4 ${panel}`}>
          {orderState === 'ready' ? <OrderHistory timeline={order.timeline} headingId="parcel-history" title="Lifecycle" /> : (
            <>
              <h3 className="font-semibold">Lifecycle</h3>
              {orderState === 'loading' ? <p role="status" className="mt-2 flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300"><Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />Loading the parcel's history…</p> : null}
              {orderState === 'error' ? <p role="alert" className="mt-2 text-sm text-warning">The history could not be loaded. Refresh to try again.</p> : null}
              {orderState === 'restricted' ? <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">The packing and delivery history is part of the order record, which your account cannot open.</p> : null}
            </>
          )}
        </section>
      </div>

      {canWriteOff && !lossOpen ? (
        <button type="button" onClick={() => setLossOpen(true)} className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-danger px-4 text-sm font-semibold text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">This parcel cannot be recovered</button>
      ) : null}
      {canWriteOff && lossOpen ? (
        <AssetLossPanel businessId={businessId}
          parcel={{ shipmentId: parcel.id, shipmentVersion: parcel.version, assignmentVersion: assignment.version }}
          onRecorded={(result) => { setLossOpen(false); onChanged(result?.alreadyRecorded
            ? 'This parcel was already written off. Nothing was recorded twice.'
            : 'Parcel written off. The rider no longer holds it. Money owed and any investigation stay open.'); }}
          onCancel={() => setLossOpen(false)} />
      ) : null}
    </article>
  );
}
